/**
 * Enum resolvers — map numeric API values to display labels.
 *
 * The mobile API returns raw integer enum values (e.g., securityOperatingMode: 0).
 * These functions convert them to human-readable labels for display.
 * They accept both number and string inputs for compatibility with mock data.
 */

// ── Security Operating Mode ──

const SECURITY_MODE_MAP: Record<number, string> = {
  0: 'Disarmed',
  1: 'Onboard',
  2: 'Away',
};

export function resolveSecurityMode(val: unknown): string {
  if (typeof val === 'number') return SECURITY_MODE_MAP[val] ?? 'Unknown';
  if (typeof val === 'string') {
    if (val === 'DISARMED' || val === '0') return 'Disarmed';
    if (val === 'ONBOARD' || val === '1') return 'Onboard';
    if (val === 'AWAY' || val === '2') return 'Away';
  }
  return 'Unknown';
}

export function resolveSecurityModeColor(val: unknown): string {
  const label = resolveSecurityMode(val);
  switch (label) {
    case 'Disarmed': return 'text-yellow-500';
    case 'Onboard': return 'text-teal-500';
    case 'Away': return 'text-green-500';
    default: return 'text-slate-500';
  }
}

export function isDisarmed(val: unknown): boolean {
  return resolveSecurityMode(val) === 'Disarmed';
}
export function isOnboard(val: unknown): boolean {
  return resolveSecurityMode(val) === 'Onboard';
}
export function isAway(val: unknown): boolean {
  return resolveSecurityMode(val) === 'Away';
}

// ── Security Arming Mode (per-zone) ──

export const ARMING_MODE_MAP: Record<number, string> = {
  0: 'Onboard & Away',
  1: 'Away Only',
  2: 'Onboard Only',
};

export function resolveArmingMode(val: unknown): string {
  if (typeof val === 'number') return ARMING_MODE_MAP[val] ?? String(val);
  if (typeof val === 'string') {
    if (val === 'ONBOARD_AND_AWAY' || val === '0') return 'Onboard & Away';
    if (val === 'AWAY' || val === '1') return 'Away Only';
    if (val === 'ONBOARD' || val === '2') return 'Onboard Only';
  }
  return String(val);
}

// ── Sensor Online State ──

const ONLINE_STATE_MAP: Record<number, string> = {
  0: 'Pending',
  1: 'Online',
  2: 'Offline',
};

export function resolveOnlineState(val: unknown): string {
  if (typeof val === 'number') return ONLINE_STATE_MAP[val] ?? 'Unknown';
  if (typeof val === 'string') {
    if (val === 'PENDING' || val === '0') return 'Pending';
    if (val === 'ONLINE' || val === '1') return 'Online';
    if (val === 'OFFLINE' || val === '2') return 'Offline';
  }
  return 'Unknown';
}

export function onlineStateVariant(val: unknown): 'success' | 'danger' | 'warning' | 'neutral' {
  const label = resolveOnlineState(val);
  if (label === 'Online') return 'success';
  if (label === 'Offline') return 'danger';
  if (label === 'Pending') return 'warning';
  return 'neutral';
}

// ── Sensor Alert State (Nevata water / freeze) ──
// Wire values are numeric (0/1/2); string forms accepted for mock data.

const ALERT_STATE_MAP: Record<number, string> = {
  0: 'Normal',
  1: 'Alert',
  2: 'Critical',
};

export function resolveAlertState(val: unknown): string {
  if (typeof val === 'number') return ALERT_STATE_MAP[val] ?? 'Unknown';
  if (typeof val === 'string') {
    if (val === 'ALERT_STATE_NORMAL' || val === '0') return 'Normal';
    if (val === 'ALERT_STATE_ALERT' || val === '1') return 'Alert';
    if (val === 'ALERT_STATE_CRITICAL' || val === '2') return 'Critical';
  }
  return 'Unknown';
}

export function isAlertNormal(val: unknown): boolean {
  return resolveAlertState(val) === 'Normal';
}

export function alertStateVariant(val: unknown): 'success' | 'warning' | 'danger' | 'neutral' {
  const label = resolveAlertState(val);
  if (label === 'Normal') return 'success';
  if (label === 'Alert') return 'warning';
  if (label === 'Critical') return 'danger';
  return 'neutral';
}

// ── Alarm Type ──

export type ZoneCategory = 'intrusion' | 'water' | 'system';
export const ALARM_TYPE_MAP: Record<number, { label: string; category: ZoneCategory }> = {
  0: { label: 'Zone', category: 'intrusion' },
  1: { label: 'Contact', category: 'intrusion' },
  2: { label: 'Motion', category: 'intrusion' },
  3: { label: 'Glass Break', category: 'intrusion' },
  4: { label: 'Smoke', category: 'intrusion' },
  5: { label: 'Fumes', category: 'intrusion' },
  6: { label: 'Radar', category: 'intrusion' },
  9: { label: 'Water', category: 'water' },
  10: { label: 'Temperature', category: 'intrusion' },
  11: { label: 'Entry', category: 'intrusion' },
  12: { label: 'Bridge', category: 'intrusion' },
  13: { label: 'Engine', category: 'intrusion' },
  14: { label: 'Galley', category: 'intrusion' },
  15: { label: 'Cockpit', category: 'intrusion' },
  16: { label: 'Locker', category: 'intrusion' },
  17: { label: 'Room', category: 'intrusion' },
  18: { label: 'Deck', category: 'intrusion' },
  19: { label: 'CO', category: 'intrusion' },
  22: { label: 'Disarm', category: 'intrusion' },
  24: { label: 'Security', category: 'intrusion' },
  25: { label: 'Smoke', category: 'intrusion' },
  26: { label: 'CO', category: 'intrusion' },
  27: { label: 'Water', category: 'water' },
  28: { label: 'Temperature', category: 'intrusion' },
  29: { label: 'Disarm', category: 'intrusion' },
  30: { label: 'Misc', category: 'intrusion' },
  31: { label: 'Misc (Siren)', category: 'intrusion' },
  32: { label: 'Geo Arm/Disarm', category: 'intrusion' },
  58: { label: 'Tamper', category: 'system' },
  59: { label: 'Inactivity', category: 'system' },
  60: { label: 'Low Battery', category: 'system' },
};

// Alarm types mobile's SecurityCommon.isSecurityTypeZone() (Common/SecurityCommon.cs)
// recognizes as belonging on the Security/Intrusion screen — a narrower allow-list than
// "category !== system/water". Notably excludes Smoke/CO/Temperature/Disarm/Misc/Misc(Siren)/
// Geo Arm-Disarm, which mobile routes to their own DeviceCategory screens instead (see
// SECURITY_DEVICE_CATEGORIES below and the sibling *_ALARM_TYPES sets further down).
const SECURITY_ZONE_ALARM_TYPES = new Set([0, 1, 2, 3, 6, 11, 12, 13, 14, 15, 16, 17, 18, 24]);

// Mobile's updateSecurityCategory (AppModels/DeviceCategoryMetrics.cs) additionally requires
// deviceCategory === DEVICE_CATEGORY_SECURITY(7) or UNKNOWN(0) — a zone reclassified via the
// Zone Type Category picker to Smoke/CO(9)/Temperature(10)/Misc(Siren)(11)/Misc(12)/etc. is
// excluded from Security even if its alarmType would otherwise qualify.
const SECURITY_DEVICE_CATEGORIES = new Set([0, 7]);
const SECURITY_DEVICE_CATEGORY_STRINGS = new Set(['UNKNOWN', 'DEVICE_CATEGORY_SECURITY']);

function alarmTypeNumeric(alarmType: unknown): number | null {
  if (typeof alarmType === 'number') return alarmType;
  if (typeof alarmType === 'string') {
    const legacyMap: Record<string, number> = {
      ALARM_TYPE_ZONE: 0, ALARM_TYPE_CONTACT: 1, ALARM_TYPE_MOTION: 2, ALARM_TYPE_GLASS_BREAK: 3,
      ALARM_TYPE_SMOKE: 4, ALARM_TYPE_FUMES: 5, ALARM_TYPE_RADAR: 6, ALARM_TYPE_WATER: 9,
      ALARM_TYPE_TEMPERATURE: 10, ALARM_TYPE_ENTRY: 11, ALARM_TYPE_BRIDGE: 12, ALARM_TYPE_ENGINE: 13,
      ALARM_TYPE_GALLEY: 14, ALARM_TYPE_COCKPIT: 15, ALARM_TYPE_LOCKER: 16, ALARM_TYPE_ROOM: 17,
      ALARM_TYPE_DECK: 18, ALARM_TYPE_CARBON_MONOXIDE: 19, ALARM_TYPE_DISARM: 22,
      ALARM_TYPE_ZONE_SECURITY_V2: 24, ALARM_TYPE_ZONE_SMOKE_V2: 25, ALARM_TYPE_ZONE_CO_V2: 26,
      ALARM_TYPE_ZONE_WATER_V2: 27, ALARM_TYPE_ZONE_TEMPERATURE_V2: 28, ALARM_TYPE_ZONE_DISARM_V2: 29,
      ALARM_TYPE_ZONE_MISC_V2: 30, ALARM_TYPE_ZONE_MISC_SIREN_V2: 31,
      ALARM_TYPE_TAMPER: 58, ALARM_TYPE_INACTIVITY: 59, ALARM_TYPE_LOW_BATT: 60,
    };
    return legacyMap[alarmType] ?? null;
  }
  return null;
}

function isSecurityDeviceCategory(deviceCategory: unknown): boolean {
  if (typeof deviceCategory === 'number') return SECURITY_DEVICE_CATEGORIES.has(deviceCategory);
  if (typeof deviceCategory === 'string') return SECURITY_DEVICE_CATEGORY_STRINGS.has(deviceCategory);
  // Not yet reported — treat like UNKNOWN so newly-discovered zones still show.
  return deviceCategory == null;
}

export function resolveAlarmType(val: unknown): string {
  if (typeof val === 'number') return ALARM_TYPE_MAP[val]?.label ?? `Type ${val}`;
  if (typeof val === 'string') {
    const numeric = alarmTypeNumeric(val);
    if (numeric != null) return ALARM_TYPE_MAP[numeric]?.label ?? val;
    return val;
  }
  return String(val);
}

function getZoneCategory(alarmType: unknown): ZoneCategory {
  if (typeof alarmType === 'number') return ALARM_TYPE_MAP[alarmType]?.category ?? 'intrusion';
  if (typeof alarmType === 'string') {
    const systemTypes = ['ALARM_TYPE_TAMPER', 'ALARM_TYPE_INACTIVITY', 'ALARM_TYPE_LOW_BATT'];
    if (systemTypes.includes(alarmType)) return 'system';
    const waterTypes = ['ALARM_TYPE_WATER', 'ALARM_TYPE_ZONE_WATER_V2'];
    if (waterTypes.includes(alarmType)) return 'water';
  }
  return 'intrusion';
}

/** System zones: tamper, inactivity, low batt, disarm, geo arm/disarm */
export function isSystemZone(alarmType: unknown): boolean {
  return getZoneCategory(alarmType) === 'system';
}

/**
 * Intrusion/security zones — mirrors mobile's updateSecurityCategory exactly:
 * alarmType must be one of SECURITY_ZONE_ALARM_TYPES (contact/motion/glass-break/entry/etc,
 * NOT smoke/CO/temperature/disarm/misc/misc-siren — those route to their own category
 * screens below) AND deviceCategory must be Security or not-yet-classified (UNKNOWN).
 * A zone reclassified via the Zone Type Category picker to Smoke/CO/Misc/etc. no longer
 * qualifies even though its alarmType alone might look "intrusion-like".
 */
export function isIntrusionZone(zone: { alarmType: unknown; deviceCategory?: unknown }): boolean {
  const numeric = alarmTypeNumeric(zone.alarmType);
  if (numeric == null || !SECURITY_ZONE_ALARM_TYPES.has(numeric)) return false;
  return isSecurityDeviceCategory(zone.deviceCategory);
}

// Mobile's updateSmokeCOCategory/updateTemperatureCategory/updateMiscSirenZoneCategory/
// updateMiscNoSirenZoneCategory (DeviceCategoryMetrics.cs) — pure alarmType filters, no
// deviceCategory gate (unlike Security). Each includes both the legacy V1 alarm code and
// the V2 zone-type code.
const SMOKE_CO_ALARM_TYPES = new Set([4, 25, 19, 26]);
const TEMPERATURE_ALARM_TYPES = new Set([10, 28]);
const MISC_SIREN_ALARM_TYPES = new Set([31]);
const MISC_NO_SIREN_ALARM_TYPES = new Set([30]);

export function isSmokeCoZone(zone: { alarmType: unknown }): boolean {
  const numeric = alarmTypeNumeric(zone.alarmType);
  return numeric != null && SMOKE_CO_ALARM_TYPES.has(numeric);
}

export function isTemperatureZone(zone: { alarmType: unknown }): boolean {
  const numeric = alarmTypeNumeric(zone.alarmType);
  return numeric != null && TEMPERATURE_ALARM_TYPES.has(numeric);
}

export function isMiscSirenZone(zone: { alarmType: unknown }): boolean {
  const numeric = alarmTypeNumeric(zone.alarmType);
  return numeric != null && MISC_SIREN_ALARM_TYPES.has(numeric);
}

export function isMiscNoSirenZone(zone: { alarmType: unknown }): boolean {
  const numeric = alarmTypeNumeric(zone.alarmType);
  return numeric != null && MISC_NO_SIREN_ALARM_TYPES.has(numeric);
}

/** Water/bilge zones: water sensor types — shown in bilge panel, not intrusion */
export function isWaterZone(alarmType: unknown): boolean {
  return getZoneCategory(alarmType) === 'water';
}

/** Any user-visible zone (intrusion + water, not system) */
export function isUserZone(alarmType: unknown): boolean {
  return !isSystemZone(alarmType);
}

/** Get the best display name for a zone: prefer descriptor, fall back to alarm type label */
export function getZoneDisplayName(zone: { sensorName?: string; descriptor?: string; alarmType?: unknown; zoneNum?: number }): string {
  // Use descriptor if it's meaningful (not empty, not generic)
  if (zone.descriptor && zone.descriptor.length > 0) {
    return zone.descriptor.charAt(0).toUpperCase() + zone.descriptor.slice(1);
  }
  // Fall back to alarm type label + zone number
  const typeLabel = resolveAlarmType(zone.alarmType);
  if (zone.zoneNum && zone.zoneNum > 0) {
    return `${typeLabel} Zone ${zone.zoneNum}`;
  }
  return typeLabel;
}

/** Get the sensor/device name (the parent xPulse or integrated unit) */
export function getZoneSensorLabel(zone: { sensorName?: string; sourceAddressMSB?: number; sourceAddressLSB?: number }): string {
  // sourceAddress 0,0 = integrated unit (not wireless)
  if (zone.sourceAddressMSB === 0 && zone.sourceAddressLSB === 0) {
    return 'Integrated';
  }
  return zone.sensorName || 'Unknown Sensor';
}

// ── Device Category ──

const DEVICE_CATEGORY_MAP: Record<number, string> = {
  0: 'Intrusion', 1: 'Location', 2: 'Geofence', 3: 'AC Power', 4: 'DC Power',
  5: 'Nevata', 6: 'Float Switch', 7: 'Security', 8: 'Electronics',
  9: 'Smoke/CO', 10: 'Temperature', 11: 'Misc (Siren)', 12: 'Misc',
  13: 'Switch', 14: 'Engine',
};

export function resolveDeviceCategory(val: unknown): string {
  if (typeof val === 'number') return DEVICE_CATEGORY_MAP[val] ?? `Category ${val}`;
  if (typeof val === 'string') {
    const stringMap: Record<string, string> = {
      UNKNOWN: 'Unknown', DEVICE_CATEGORY_SECURITY: 'Security',
      DEVICE_CATEGORY_SMOKE_CO: 'Smoke/CO', DEVICE_CATEGORY_TEMPERATURE: 'Temperature',
      DEVICE_CATEGORY_MISC_SIREN: 'Misc (Siren)', DEVICE_CATEGORY_MISC_NO_SIREN: 'Misc',
      DEVICE_CATEGORY_SWITCH: 'Switch', DEVICE_CATEGORY_ENGINE_COWLING: 'Engine',
      DEVICE_CATEGORY_FLOAT_SWITCH: 'Float Switch', DEVICE_CATEGORY_ELECTRONICS: 'Electronics',
      DEVICE_CATEGORY_AC_POWER: 'AC Power', DEVICE_CATEGORY_DC_POWER: 'DC Power',
      DEVICE_CATEGORY_NEVATA: 'Nevata', DEVICE_CATEGORY_LOCATION: 'Location',
      DEVICE_CATEGORY_GEOFENCE: 'Geofence',
    };
    return stringMap[val] ?? val;
  }
  return String(val);
}

// ── Geofence Auto-Arm Setting ──

export const GEO_AUTO_ARM_MAP: Record<number, string> = {
  0: 'Disabled', 1: 'Sync with Onboard', 2: 'Sync with Away', 3: 'Sync with All Modes',
};

export function resolveGeoAutoArm(val: unknown): string {
  if (typeof val === 'number') return GEO_AUTO_ARM_MAP[val] ?? `Setting ${val}`;
  if (typeof val === 'string') {
    const stringMap: Record<string, string> = {
      GEOFENCE_AUTO_ARM_DISARM_DISABLED: 'Disabled',
      GEOFENCE_AUTO_ARM_DISARM_ONBOARD_MODE: 'Sync with Onboard',
      GEOFENCE_AUTO_ARM_DISARM_AWAY_MODE: 'Sync with Away',
      GEOFENCE_AUTO_ARM_DISARM_ALL_MODES: 'Sync with All Modes',
    };
    return stringMap[val] ?? val;
  }
  return String(val);
}

// ── Remote Siren Sync Mode ──

// Labels match XPulseSettingsPageViewModel.cs's SelectionList exactly (the only
// mobile page that shows this setting) — not generic paraphrases.
export const SIREN_SYNC_MAP: Record<number, string> = {
  0: 'Disabled', 1: 'Critical + Security Events', 3: 'Security Events',
};

export function resolveSirenSyncMode(val: unknown): string {
  if (typeof val === 'number') return SIREN_SYNC_MAP[val] ?? `Mode ${val}`;
  if (typeof val === 'string') {
    if (val === 'DISABLED') return 'Disabled';
    if (val === 'ENABLED') return 'Critical + Security Events';
    if (val === 'ALARM_ENABLED') return 'Security Events';
  }
  return String(val);
}

// ── Event Message Type ──

const EVENT_TYPE_MAP: Record<number, { label: string; color: string }> = {
  0: { label: 'System', color: 'slate' },
  1: { label: 'Alert', color: 'red' },
  2: { label: 'Verification', color: 'blue' },
  3: { label: 'Status', color: 'green' },
  4: { label: 'Geofence', color: 'blue' },
  5: { label: 'Tracking', color: 'teal' },
  6: { label: 'Sensor', color: 'orange' },
  7: { label: 'Security', color: 'yellow' },
  8: { label: 'System', color: 'slate' },
  9: { label: 'DC', color: 'orange' },
  10: { label: 'AC', color: 'orange' },
  11: { label: 'Bilge', color: 'blue' },
  12: { label: 'Switch', color: 'slate' },
  13: { label: 'VMS', color: 'purple' },
  14: { label: 'Tender', color: 'teal' },
};

export function resolveEventType(val: unknown): string {
  if (typeof val === 'number') return EVENT_TYPE_MAP[val]?.label ?? `Event ${val}`;
  if (typeof val === 'string') {
    // Handle string enum names
    return val.replace('MESSAGE_TYPE_', '').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  }
  return String(val);
}

export function eventTypeColor(val: unknown): string {
  if (typeof val === 'number') return EVENT_TYPE_MAP[val]?.color ?? 'slate';
  return 'slate';
}

// ── Alarm type icon path ──

export function alarmTypeIconPath(alarmType: unknown): string {
  const label = resolveAlarmType(alarmType);
  switch (label) {
    case 'Motion':
      return 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z';
    case 'Smoke':
      return 'M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z';
    case 'CO':
      return 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z';
    case 'Water':
      return 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25';
    case 'Temperature':
      return 'M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 6.51 6.51 0 009 4.5a.75.75 0 01.721.544l1.515 5.063a.956.956 0 001.526.47L15.362 5.214z';
    case 'Misc':
    case 'Misc (Siren)':
      return 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z';
    case 'Tamper':
    case 'Inactivity':
    case 'Low Battery':
      return 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126z';
    default:
      return 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21';
  }
}
